import {
  MutationCache,
  QueryCache,
  QueryClient,
} from "@tanstack/react-query";

import { ErrorHandler } from "@/shared/api/error-handler";
import { API_STATUS_CODES, REACT_QUERY_CONFIG, MUTATION_CONFIG } from "@/shared/config";

// Singleton только для браузера — на сервере шеринг между запросами
// приводит к межзапросной утечке кэша (см. KAN-320)
let browserQueryClient: QueryClient | null = null;

const isMeQueryKey = (queryKey: unknown) => {
  if (!Array.isArray(queryKey)) return false;
  return queryKey.some((entry) => {
    if (typeof entry !== "object" || entry === null || !("url" in entry)) {
      return false;
    }
    const url = (entry as { url?: unknown }).url;
    return typeof url === "string" && url.startsWith("/me");
  });
};

const createQueryClient = () => {
  const client = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Читаем статус локально: модуль низкоуровневый, а error-handler в
        // тестах мокается целиком (query-client.test.ts) — импорт хелпера
        // сделал бы поведение зависимым от мока.
        const status =
          (error as { response?: { status?: number } } | null)?.response
            ?.status ?? (error as { status?: number } | null)?.status;
        const isMeQuery = isMeQueryKey(query.queryKey);
        if (status === 404) {
          return;
        }
        // Для ошибок 500 удаляем запрос из кеша, чтобы не использовать старые данные
        if ((status ?? 0) >= 500) {
          client.removeQueries({ queryKey: query.queryKey });
        }
        if (!isMeQuery) {
          ErrorHandler.getInstance().handleSilentError(error, {
            component: "TanStackQuery",
            action: "query",
          });
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        // Мутация сама показывает ошибку пользователю (например, форма входа —
        // строкой по коду ответа). Второй тост отсюда был бы дублем (KAN-1216).
        if (mutation.meta?.["skipGlobalErrorHandler"]) return;
        ErrorHandler.getInstance().handleError(error, {
          component: "TanStackQuery",
          action: "mutation",
        });
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: REACT_QUERY_CONFIG.DEFAULT_STALE_TIME,
        gcTime: REACT_QUERY_CONFIG.DEFAULT_GC_TIME,
        retry: (failureCount, error) => {
          const axiosError = error as {
            response?: { status?: number; config?: { url?: string } };
            config?: { url?: string };
          };
          const status = axiosError?.response?.status ?? (error as { status?: number })?.status;
          const url = axiosError?.response?.config?.url ?? axiosError?.config?.url ?? "";

          if (typeof status === "number" && status >= API_STATUS_CODES.BAD_REQUEST && status < 500) {
            return false;
          }

          if (!status || status === 0) {
            const isMeOrAuth =
              typeof url === "string" &&
              (url.startsWith("/me") || url.startsWith("/auth") || url.startsWith("/oauth"));
            if (isMeOrAuth) return false;
            const isNetworkError =
              (error as { code?: string; message?: string })?.code === "ECONNABORTED" ||
              (error as { message?: string })?.message?.includes("timeout") ||
              (error as { message?: string })?.message?.includes("Network Error");
            if (isNetworkError) {
              return failureCount < 3;
            }
          }

          if (typeof status === "number" && status >= 500) {
            return false;
          }

          return failureCount < 2;
        },
        retryDelay: (attemptIndex) =>
          Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        refetchOnMount: false,
        networkMode: "online",
      },
      mutations: {
        retry: MUTATION_CONFIG.RETRY,
        networkMode: MUTATION_CONFIG.NETWORK_MODE,
      },
    },
  });

  return client;
};

export const getQueryClient = () => {
  if (typeof window === "undefined") {
    // Сервер: свежий клиент на каждый вызов — кэш не должен жить дольше запроса
    return createQueryClient();
  }
  if (!browserQueryClient) {
    browserQueryClient = createQueryClient();
  }
  return browserQueryClient;
};
